import { sum } from 'lodash';
import { sortBy } from 'lodash';
import { uniqBy } from 'lodash';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { useCallback } from 'react';
import { useMemo } from 'react';
import React from 'react';
import { Select } from '../../../../base-components/Select';
import { ExtraProps } from '../../../../base-components/Select';
import { ProcedureTemplate } from '../../../../types/entities';
import { notEmpty } from '../../../../utils';
import { useMatchesLabel } from '../../hooks/useMatchesLabel';

interface ProcedureTemplateOption {
    value: string;
    label: string;
    groupIds: number[];
}

export interface Props {
    instanceId: string;
    procedureTemplates: ProcedureTemplate[];
    value: ProcedureTemplate[];
    onChange: (value: ProcedureTemplate[]) => void;
    className?: string;
    appearance?: ExtraProps['appearance'];
    matches?: { [k: string]: number };
}

/**
 * Селектор шаблона способа закупки
 */
export const SelectProcedureTemplate: React.FC<Props> = ({
    instanceId,
    procedureTemplates,
    value,
    onChange,
    className,
    appearance,
    matches,
}) => {
    const groupedProcedureTemplates = useMemo(
        () =>
            sortBy(
                Object.values(groupBy(procedureTemplates, 'title')).map(
                    (groupValues) => ({
                        groupId: map(groupValues, 'id').join(','),
                        title: groupValues[0].title,
                        procedureTemplateIds: map(groupValues, 'id'),
                    })
                ),
                'title'
            ),
        [procedureTemplates]
    );

    const selectOptions: ProcedureTemplateOption[] = useMemo(
        () =>
            map(groupedProcedureTemplates, (groupedProcedureTemplate) => ({
                value: groupedProcedureTemplate.groupId,
                label: groupedProcedureTemplate.title,
                groupIds: groupedProcedureTemplate.procedureTemplateIds,
                matches: matches
                    ? sum(
                          groupedProcedureTemplate.procedureTemplateIds.map(
                              (id) => matches[id] || 0
                          )
                      )
                    : 0,
            })),
        [groupedProcedureTemplates, matches]
    );

    const selectValue: ProcedureTemplateOption[] = useMemo(
        () =>
            uniqBy(
                value
                    .map((valueItem) =>
                        find(selectOptions, (option) =>
                            option.groupIds.includes(valueItem.id)
                        )
                    )
                    .filter(notEmpty),
                'value'
            ),
        [selectOptions, value]
    );

    const procedureTemplatesById = useMemo(
        () => keyBy(procedureTemplates, 'id'),
        [procedureTemplates]
    );

    const handleChange = useCallback(
        (nextSelectedOptions: readonly ProcedureTemplateOption[]) => {
            const nextValue: ProcedureTemplate[] = nextSelectedOptions
                .flatMap((selectedOption) =>
                    selectedOption.groupIds.map(
                        (id) => procedureTemplatesById[id]
                    )
                )
                .filter(notEmpty);
            onChange?.(nextValue);
        },
        [onChange, procedureTemplatesById]
    );

    const formatOptionLabel = useMatchesLabel<ProcedureTemplateOption>();

    return (
        <Select
            instanceId={instanceId}
            placeholder="Способ закупки"
            value={selectValue}
            onChange={handleChange}
            options={selectOptions}
            className={className}
            isMulti={true}
            isSearchable={true}
            isClearable={false}
            appearance={appearance}
            formatOptionLabel={formatOptionLabel}
        />
    );
};
